import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, model, OnInit, signal, TemplateRef, untracked, viewChild, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { IShowConfirmationDialogInput, simpleDeepClone } from '@phx-erp/shared';
import { NgxUiLoaderModule } from 'ngx-ui-loader';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { Dialog, DialogModule } from 'primeng/dialog';
import { ListboxModule } from 'primeng/listbox';
import { SkeletonModule } from 'primeng/skeleton';
import { SplitterModule } from 'primeng/splitter';
import { TooltipModule } from 'primeng/tooltip';
import { lastValueFrom } from 'rxjs';
import { CalculateListHeightPipe } from '../../../../@core/pipes/calculate-list-height.pipe';
import { BaseListCombinedService } from '../../../../@core/services/base-list-combined.service';
import { Address, CustomerPriceList, CustomerPriceListAddressLink, CustomerPriceListAddressLinkInput, DeletionResult, Operator, PriceListEntry, PriceListType, Product, SearchInput } from '../../../../generated/generated-types';
import { AddressDropDownComponent } from '../../../crm/modules/company-drop-down/company-drop-down/address-drop-down.component';
import { CustomerPriceListAddressLinkService } from '../../services/customer-price-list-address-link.service';
import { CustomerPriceListService } from '../../services/customer-price-list.service';
import { PriceListEntryService } from '../../services/price-list-entry.service';
import { BulkPriceListEntryListWidgetComponent } from '../bulk-price-list-entry-list-widget/bulk-price-list-entry-list-widget.component';
import { CustomerPriceListDetailWidgetComponent } from '../customer-price-list-detail-widget/customer-price-list-detail-widget.component';
import { PriceListDisplayRowWidgetComponent } from '../price-list-display-row-widget/price-list-display-row-widget.component';
import { PriceListEntryListWidgetComponent } from '../price-list-entry-list-widget/price-list-entry-list-widget.component';
import { HandlePriceListoptions } from '../vendor-product-price-edit-widget/type-interfaces';

@Component({
  selector: 'app-customer-product-price-edit-widget',
  templateUrl: './customer-product-price-edit-widget.component.html',
  styleUrls: ['./customer-product-price-edit-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    ButtonModule,
    SplitterModule,
    RouterModule,
    BadgeModule,
    TooltipModule,
    PriceListEntryListWidgetComponent,
    CalculateListHeightPipe,
    PriceListDisplayRowWidgetComponent,
    DialogModule,
    AddressDropDownComponent,
    NgxUiLoaderModule,
    SkeletonModule,
    ListboxModule,
    FormsModule,
  ]
})
export class CustomerProductPriceEditWidgetComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  private baseServices = inject(BaseListCombinedService);
  private customerPriceListService = inject(CustomerPriceListService);
  private customerPriceListAddressLinkService = inject(CustomerPriceListAddressLinkService);
  private priceListEntryService = inject(PriceListEntryService);
  private currentPriceEntryRequestId = 0;

  product = model.required<Product>();

  customerPriceListAddressLinkSearchInput: SearchInput;

  dataTestid = input<string>("customer-product-price-edit-widget");

  PriceListType = PriceListType;


  customerPriceLists: CustomerPriceList[] = [];
  selectedPriceList: CustomerPriceList;
  priceListEntries: Partial<PriceListEntry>[] = [];
  priceListEntriesOld: PriceListEntry[];
  savedPriceListEntry: PriceListEntry;
  selectedAddress: Address;
  // fallbackFields: FormlyFieldConfig[];
  topRightPaneSize: number = 100;
  bottomRightPaneSize: number = 0;

  @ViewChild('bulkPrices')
  public bulkPrices: BulkPriceListEntryListWidgetComponent;

  @ViewChild('detailTemplate')
  public detailTemplate: TemplateRef<any>;

  @ViewChild('detailWidget')
  public detailWidget: CustomerPriceListDetailWidgetComponent;

  @ViewChild('transferDialog')
  public transferDialog: Dialog;

  // public editSettings: Object;
  public toolbar: string[];
  public field: Object = null;

  copyAddressSig = signal<boolean>(false);
  minHeightInPt = signal<number>(200);
  historyCheckedSig = signal<boolean>(false);
  copyOrEditPriceListDialogVisible = signal<boolean>(false) // the dialog to choose the customer to copy to or to edit
  selectedAddressSig = signal<Address>(null);
  fieldsSig = signal<CustomerPriceListAddressLink[]>([]);
  customerPriceListNodeSelectedSig = signal<CustomerPriceListAddressLink>(null);
  currentPriceEntriesByPriceListIdSig = signal<Record<string, PriceListEntry>>({});

  priceListEntryListWidget = viewChild<PriceListEntryListWidgetComponent>('priceListEntryListWidget');
  priceListEntryHistoryListWidget = viewChild<PriceListEntryListWidgetComponent>('priceListEntryHistoryListWidget');
  addressDropDown = viewChild<AddressDropDownComponent>('addressDropDown');
  chooseDialogSig = viewChild<Dialog>('chooseDialog');

  isLoadingSig = signal<boolean>(true); //we load always right in the beginning

  // Price reference product computed signals
  hasPriceReferenceProduct = computed(() => {
    const product = this.product();
    return !!(product?.priceReferenceProduct || product?.priceReferenceProductId);
  });

  priceReferenceProductIdentifier = computed(() => {
    const product = this.product();
    return (product?.priceReferenceProduct?.identifier ?? '') + ' - ' + (product?.priceReferenceProduct?.description ?? '');
  });

  priceReferenceProductId = computed(() => {
    const product = this.product();
    return product?.priceReferenceProduct?.id ?? '';
  });

  priceReferenceText = computed(() => {
    const identifier = this.priceReferenceProductIdentifier();
    return identifier ? this.baseServices.i18nService.translate(_('product.prices-of')) + ' ' + identifier : '';
  });

  /**
   * Open the price reference product in a new tab
   */
  openInNewTab() {
    const productId = this.priceReferenceProductId();
    if (productId) {
      window.open(`/products/edit/${productId}`, '_blank');
    }
  }

  /**
 * Effective product id to use for loading customer relations and entries.
 * If purchase prices are inherited, use the referenced product id.
 */
  effectiveCustomerProductId = computed(() => {
    const product = this.product();
    if (this.hasPriceReferenceProduct()) {
      return product?.priceReferenceProductId ?? product?.priceReferenceProduct?.id ?? product?.id;
    }
    return product?.id;
  });

  constructor() {
    // effect(() => {
    //   if (this.productId()) {
    //     console.log('Has Changed Product ID: ', this.productId());
    //   }
    //   if (this.fieldsSig().length > 0) {
    //     console.log('Fields: ', this.fieldsSig());
    //   }
    //   if (this.customerPriceListNodeSelectedSig()) {
    //     console.log('PriceListNodeSelected: ', this.customerPriceListNodeSelectedSig());
    //   }
    //   if (this.selectedAddressSig()) {
    //     console.log('Selected Address: ', this.selectedAddressSig());
    //   }
    // });
    // this.destroyRef.onDestroy(() => {
    //   console.log(`Destroying ${this.constructor.name}`);
    // });
    // afterNextRender({
    //   write: () => {
    //     this.pListboxSig().multiple = false;
    //     this.pListboxSig().onChange.subscribe((event: ListboxChangeEvent) => {
    //       this.customerPriceListNodeSelectedSig.set(event.value);
    //       this.loadItemsOfPriceList();
    //     });
    //   }
    // },);
    effect(() => {
      const priceLists = this.fieldsSig();
      const selected = this.customerPriceListNodeSelectedSig();
      if (priceLists.length === 0) {
        if (selected) {
          untracked(() => this.customerPriceListNodeSelectedSig.set(null));
        }
        return;
      }
      // Keep a valid row if the current list was removed. Do not fill an empty
      // selection — Ctrl/Cmd+click is the intentional unselect path.
      if (selected && !priceLists.some(list => list.id === selected.id)) {
        untracked(() => this.customerPriceListNodeSelectedSig.set(priceLists[0]));
      }
    });
    effect(() => {
      const priceListLinks = this.fieldsSig();
      const productId = this.effectiveCustomerProductId();
      untracked(() => {
        void this.loadCurrentPriceEntries(priceListLinks, productId);
      });
    });
  }

  async ngOnInit() {

    const productIdForCustomers = this.effectiveCustomerProductId();
    if (productIdForCustomers) {
      this.customerPriceListAddressLinkSearchInput = {
        searchFilter: {
          rules: [
            {
              field: "productId",
              operator: Operator.equal,
              value: productIdForCustomers
            }
          ]
        },
      };
    }

    this.isLoadingSig.set(true); //should already be true
    let res = await lastValueFrom(this.customerPriceListAddressLinkService.getItems(this.customerPriceListAddressLinkSearchInput).mapSingle(x => x.getCustomerPriceListAddressLinks));
    (res.items as CustomerPriceListAddressLink[]).map(x => {
      this.fieldsSig.update((field) => {
        // Ensure field is always an array
        field = field || [];
        return [...field, x];
      });
    });
    this.customerPriceListNodeSelectedSig.set(this.fieldsSig()[0] ?? null);
    this.isLoadingSig.set(false);
  }

  async loadItemsOfPriceList() {

    if (!this.customerPriceListNodeSelectedSig())
      return;

    //TODO filtering - only show current & future prices
    let res = await lastValueFrom(this.priceListEntryService.getItems({
      searchFilter: {
        rules: [
          {
            field: "priceListId",
            operator: Operator.equal,
            value: this.customerPriceListNodeSelectedSig().customerPriceList?.id ?? this.customerPriceListNodeSelectedSig().customerPriceListId
          }, {

          }
        ]
      },
      onlyActive: true,
      onlyHistory: false,
      productIds: [this.product()?.id]
    }).mapSingle(x => x.getPriceListEntries));
    let items = res.items as PriceListEntry[];
    this.priceListEntries = simpleDeepClone(items);
  }

  currentPriceEntryFor(priceListLink: CustomerPriceListAddressLink): PriceListEntry | null {
    const priceListId = priceListLink?.customerPriceList?.id ?? priceListLink?.customerPriceListId;
    return priceListId ? this.currentPriceEntriesByPriceListIdSig()[priceListId] ?? null : null;
  }

  private async loadCurrentPriceEntries(priceListLinks: CustomerPriceListAddressLink[], productId: string | null): Promise<void> {
    const requestId = ++this.currentPriceEntryRequestId;
    const priceListIds = priceListLinks
      .map(priceListLink => priceListLink?.customerPriceList?.id ?? priceListLink?.customerPriceListId)
      .filter((id): id is string => !!id);

    if (!productId || priceListIds.length === 0) {
      this.currentPriceEntriesByPriceListIdSig.set({});
      return;
    }

    try {
      const priceEntriesResult = await lastValueFrom(this.priceListEntryService.getItems({
        productIds: [productId],
        onlyActive: true,
        onlyOfPriceListIds: priceListIds,
        take: 0
      }).mapSingle(x => x.getPriceListEntries));

      if (requestId !== this.currentPriceEntryRequestId) {
        return;
      }

      const today = new Date();
      const priceEntriesByPriceListId = (priceEntriesResult.items as PriceListEntry[]).reduce<Record<string, PriceListEntry>>((result, priceEntry) => {
        const priceListId = priceEntry.priceListId;
        const startDate = priceEntry.startDate ? new Date(priceEntry.startDate) : null;
        const currentEntry = priceListId ? result[priceListId] : null;
        const currentStartDate = currentEntry?.startDate ? new Date(currentEntry.startDate) : null;
        const isStarted = !startDate || startDate <= today;
        const isNewerThanCurrent = !currentStartDate || (startDate && startDate > currentStartDate);

        if (priceListId && isStarted && isNewerThanCurrent && priceEntry.price !== null && priceEntry.price !== undefined) {
          result[priceListId] = priceEntry;
        }
        return result;
      }, {});

      this.currentPriceEntriesByPriceListIdSig.set(priceEntriesByPriceListId);
    } catch (error: any) {
      if (requestId === this.currentPriceEntryRequestId) {
        this.currentPriceEntriesByPriceListIdSig.set({});
      }
      console.error('Failed to load active customer product prices for price list overview', error);
    }
  }

  addNewEmptyEntry() {
    this.priceListEntries = [{}, ...this.priceListEntries];
  }

  async saveEntry(entry: PriceListEntry) {

    entry.priceListId = this.customerPriceListNodeSelectedSig().customerPriceList?.id ?? this.customerPriceListNodeSelectedSig().customerPriceListId;
    entry.productId = this.product()?.id;

    let date = new Date(entry.startDate);
    let now = new Date();

    if (date.getTime() > 0 && date.getDate() < now.getDate()) {
      let confirmationInput: IShowConfirmationDialogInput = {
        title: "Das Datum liegt in der Vergangenheit, sicher?",
        message: "",
        btnYesText: _("common.yes"),
        btnNoText: _("common.no")
      };
      let sub = this.baseServices.modalService.showConfirmationDialog(confirmationInput)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(async result => {
          if (result == true) {
            // delete entry.priceListId;
            this.savedPriceListEntry = await this.priceListEntryService.saveItem(entry);
            this.priceListEntries = this.priceListEntries.filter(x => x.id != entry.id);
            this.priceListEntries = [...this.priceListEntries];
            // this.cdRef.markForCheck()
            this.baseServices.notificationService.success(_("common.saved"));
            this.priceListEntryListWidget()?.refresh();
            sub.unsubscribe();
          } else {
            this.loadItemsOfPriceList();
            sub.unsubscribe();
          }
        });
      // let ref = this.modalService.show(ConfirmationDialogComponent, { initialState: { message: "Das Datum liegt in der Vergangenheit, sicher?" } });
      // (ref.content as ConfirmationDialogComponent).onClose.subscribe(async result => {
      //   if (result == true) {
      //     // delete entry.priceListId;
      //     this.savedPriceListEntry = await this.priceListEntryService.saveItem(entry);
      //     this.priceListEntries = this.priceListEntries.filter(x => x.id != entry.id);
      //     this.priceListEntries = [...this.priceListEntries];
      //     // this.cdRef.markForCheck()
      //     this.baseServices.notificationService.success(_("common.saved"));
      //     this.priceListEntryListWidget()?.refresh();

      //   } else {
      //     this.loadItemsOfPriceList();
      //   }
      // })
    }

    else {
      try {
        this.savedPriceListEntry = await this.priceListEntryService.saveItem(entry);
        this.priceListEntries = this.priceListEntries.filter(x => x.id != entry.id);
        this.priceListEntries = [this.savedPriceListEntry, ...this.priceListEntries];
        // this.cdRef.markForCheck()
        this.priceListEntryListWidget()?.refresh();
        this.baseServices.notificationService.success(_("common.saved"));
      }
      catch (e) {
        this.loadItemsOfPriceList();
      }
    }
  }

  async deleteEntry(entry: PriceListEntry[]) {
    for (let i = 0; i < entry.length; i++) {
      const element = entry[i];
      await this.priceListEntryService.deleteItem(element.id);
      this.priceListEntries = this.priceListEntries.filter(x => x.id != element.id);
    }
    this.baseServices.notificationService.success(_("common.deleted"));
  }

  async onNewPriceListSaved(customerPriceListAddressLink: CustomerPriceListAddressLink) {
    this.fieldsSig.update((field) => {
      field = field || [];
      return [...field, customerPriceListAddressLink];
    });
  }

  async deleteCustomerPriceListWithConfirm() {
    let msg = this.baseServices.i18nService.translate(_("common.delete-customer-price-list-confirm"));
    let sub = this.baseServices.modalService.showDeletionDialog(msg)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async result => {
        if (result == true) {
          let deletitionResponse = await this.customerPriceListAddressLinkService.deleteItemByListAndProductId(this.customerPriceListNodeSelectedSig().customerPriceList?.id, this.product()?.id);
          if (deletitionResponse.result == DeletionResult.DELETED) {
            this.baseServices.notificationService.success(_("common.deleted"));
            const deletedPriceListId = this.customerPriceListNodeSelectedSig()?.customerPriceList?.id;
            this.fieldsSig.update((field) => {
              field = field || [];
              return field.filter(x => x.customerPriceList?.id != deletedPriceListId);
            });
            this.customerPriceListNodeSelectedSig.set(this.fieldsSig()[0] ?? null);
            sub.unsubscribe();
          }
        } else {
          sub.unsubscribe();
          return
        }
      });

  }

  async handlePriceList(option: HandlePriceListoptions) {

    switch (option) {
      case "save":
        let addresses = [];
        if (this.selectedAddressSig())
          addresses = [this.selectedAddressSig()];
        let ref = this.baseServices.modalService.fromComponent(CustomerPriceListDetailWidgetComponent, {
          size: "sm",
          verticalAlign: 'top',
          closable: true,
          title: this.baseServices.i18nService.translate(_('price-list.create')),
          locals: {
            address: null,
          },
          setLocalsSig: {
            productId: this.product()?.id,
          }
        });
        let sub = ref.subscribe((data: CustomerPriceListAddressLink) => {
          if (data) {
            this.onNewPriceListSaved(data);
          }
          sub.unsubscribe();
        });
        break
      case "copy":
        this.chooseDialogSig().header = this.baseServices.i18nService.translate(_('customer-price-list.choose-customer'));
        this.copyOrEditPriceListDialogVisible.set(true);
        this.addressDropDown().formControl.setValue({}); //Bitte auswählen!
        this.addressDropDown().props.placeholder = this.baseServices.i18nService.translate(_('common.please-select'));
        this.copyAddressSig.set(true);
        break
      case "edit":
        this.chooseDialogSig().header = this.baseServices.i18nService.translate(_('customer-price-list.edit-customer'));
        this.selectedAddressSig.set(this.customerPriceListNodeSelectedSig().address);
        this.addressDropDown().formControl.setValue(this.selectedAddressSig());
        this.fieldsSig.update((field) => {
          field = field || [];
          return field.filter(x => x.id != this.selectedAddressSig().id);
        });
        this.copyOrEditPriceListDialogVisible.set(true);
        break
    }

  }

  async copyOrSaveCustomerPriceList(address: Address) {
    if (!address)
      this.baseServices.notificationService.warning(_("customer-price-list.choose-address-first"));

    if (this.copyAddressSig()) {
      // let selectedPriceList = await this.customerPriceListService.copyCustomerPriceList({ id: this.customerPriceListNodeSelectedSig().id, addressId: address.id })
      // this.customerPriceListNodeSelectedSig.set(selectedPriceList);
      // this.baseServices.notificationService.success(_("common.copied"));
      // this.fieldsSig.update((field) => {
      //   field = field || [];
      //   return [...field, selectedPriceList];
      // });
      // this.copyAddressSig.set(false);
      // this.copyOrEditPriceListDialogVisible.set(false);
    }
    else { // the edit branch of this method
      let customerPriceListInput: CustomerPriceListAddressLinkInput = {
        customerPriceListId: this.customerPriceListNodeSelectedSig()?.customerPriceList?.id ?? this.customerPriceListNodeSelectedSig()?.customerPriceListId,
        addressId: address?.id ?? this.selectedAddress?.id,
        productId: this.product()?.id ?? this.customerPriceListNodeSelectedSig().productId,
        id: this.customerPriceListNodeSelectedSig().id
      };
      let saved: CustomerPriceListAddressLink = await this.customerPriceListAddressLinkService.saveItem(customerPriceListInput);

      if (saved) {
        this.fieldsSig.update((field) => {
          field = field || [];
          return field.filter(x => x.id != this.customerPriceListNodeSelectedSig().id);
        });
        this.fieldsSig.update((field) => {
          field = field || [];
          return [...field, saved];
        });
        this.baseServices.notificationService.success(_("common.changed"));
        this.copyOrEditPriceListDialogVisible.set(false);
      }

    }
  }

  addressAlreadyLinked(address: Address): boolean {
    if (address == null) return false;
    return this.getAddressesFromFields().find(x => x.id == address.id) ? true : false;
  }

  getAddressesFromFields(): Address[] {
    let addresses: Address[] = [];
    this.fieldsSig().forEach((field) => {
      if (field?.address)
        addresses.push(field.address);
    });
    return addresses;
  }

  setSelectedAddress(address: Address | Address[]) {
    if (Array.isArray(address)) {
      this.selectedAddressSig.set(address[address.length - 1]);
    } else {
      this.selectedAddressSig.set(address);
    }
  }

}


